"use client";

import React from "react";
import Image from "next/image";
import PageLayout from "../PageLayout/PageLayout";
import { Text, Title } from "@digitaltableteur/react";
import styles from "./StoryBlock.module.css";

/**
 * Represents a single image with caption
 */
export interface StoryImage {
  /** Image source path */
  src: string;
  /** Alt text for the image */
  alt: string;
  /** Image width in pixels */
  width: number;
  /** Image height in pixels */
  height: number;
  /** Optional caption text */
  caption?: string;
  /** Optional CSS mix-blend-mode */
  mixBlendMode?: "multiply" | "normal";
}

/**
 * Props for the StoryBlock component
 */
export interface StoryBlockProps {
  /** Optional subtitle/category label */
  subtitle?: string;
  /** Main title of the story section */
  title: string;
  /** Body content as array of paragraphs or React nodes */
  content: React.ReactNode[];
  /**
   * Image or images to display. Without imageLayout they render as single
   * (one image) or grid (several); imageLayout="none" hides them.
   */
  images?: StoryImage | StoryImage[];
  /**
   * Image layout: "single" (1 col), "grid" (2 col when there is more than one
   * image) or "none" (images hidden). When omitted, passed images render as
   * "single" for one image and "grid" for several.
   */
  imageLayout?: "single" | "grid" | "none";
  /**
   * Background color variant; defaults to transparent. It colours the root
   * element and never changes which element that is.
   */
  backgroundColor?: "light" | "white" | "transparent";
  /** Maximum width constraint; defaults to md. */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "full";
  /** Spacing variant; defaults to comfortable. */
  spacing?: "compact" | "default" | "comfortable" | "spacious";
  /** Additional CSS class */
  className?: string;
  /**
   * Root element, also when a background color is set. The inner PageLayout
   * wrapper is always a <div>, so sections never nest. Defaults to "section".
   */
  as?: "section" | "article" | "div";
  /**
   * Accessible label for the section or article (defaults to the title). Not
   * applied when as="div", since a generic element cannot be named.
   */
  ariaLabel?: string;
  /**
   * Heading level for the section title; visual size is unchanged. Defaults
   * to 2 (a section directly under the page h1); pass 3 or 4 when the block
   * sits under a deeper heading.
   */
  headingLevel?: 2 | 3 | 4;
}

/**
 * StoryBlock - A reusable design system component for narrative content sections
 *
 * Displays a story section with optional subtitle, title, body copy, and images.
 * Supports various layouts: text-only, single image, or grid of images.
 * Commonly used in case studies and work pages for project narratives.
 *
 * @example
 * ```tsx
 * <StoryBlock
 *   subtitle="Discovery Process"
 *   title="Research and Analysis"
 *   content={[
 *     <Text size="s">First paragraph...</Text>,
 *     <Text size="s">Second paragraph...</Text>,
 *   ]}
 *   images={[
 *     { src: "/image1.png", alt: "Image 1", width: 738, height: 506, caption: "Caption 1" },
 *     { src: "/image2.png", alt: "Image 2", width: 738, height: 506, caption: "Caption 2" },
 *   ]}
 *   imageLayout="grid"
 *   backgroundColor="light"
 * />
 * ```
 */
const StoryBlock: React.FC<StoryBlockProps> = ({
  subtitle,
  title,
  content,
  images,
  imageLayout,
  backgroundColor = "transparent",
  maxWidth = "md",
  spacing = "comfortable",
  className = "",
  as = "section",
  ariaLabel,
  headingLevel = 2,
}) => {
  const Wrapper = as;
  const bgColors = {
    light: "var(--color-light-bg)",
    white: "var(--color-white)",
    transparent: "transparent",
  };

  const imagesArray = Array.isArray(images) ? images : images ? [images] : [];
  // Without an explicit layout, passed images show: one as single, several as grid.
  const resolvedImageLayout =
    imageLayout ?? (imagesArray.length > 1 ? "grid" : "single");
  const hasImages = imagesArray.length > 0 && resolvedImageLayout !== "none";
  const gridClass =
    resolvedImageLayout === "grid" && imagesArray.length > 1
      ? styles.grid2Col
      : styles.grid1Col;

  const wrapperStyle =
    backgroundColor !== "transparent"
      ? { backgroundColor: bgColors[backgroundColor] }
      : undefined;

  return (
    <Wrapper
      className={`${styles.storyBlock} ${className}`}
      style={wrapperStyle}
      aria-label={as === "div" ? undefined : ariaLabel || title}
    >
      <PageLayout maxWidth={maxWidth} spacing={spacing} as="div">
        <div className={styles.grid1Col}>
          <div className={styles.col}>
            {subtitle && (
              // Kicker, not a heading: it must not outrank or precede the real
              // section title in the outline AT users navigate by.
              <Text size="s" className={styles.subtitle}>
                {subtitle}
              </Text>
            )}
            <Title level={headingLevel} size="s" className={styles.title}>
              {title}
            </Title>
            {content.map((paragraph, index) => (
              <div key={index} className={styles.paragraph}>
                {paragraph}
              </div>
            ))}
          </div>
        </div>

        {hasImages && (
          <div className={`${gridClass} ${styles.imagesRow}`}>
            {imagesArray.map((image, index) => (
              <figure key={index} className={`${styles.col} ${styles.figure}`}>
                <Image
                  src={image.src}
                  alt={image.alt}
                  width={image.width}
                  height={image.height}
                  sizes="(max-width: 768px) 100vw, 768px"
                  style={{
                    width: "100%",
                    height: "auto",
                    ...(image.mixBlendMode && {
                      mixBlendMode: image.mixBlendMode,
                    }),
                  }}
                />
                {image.caption && (
                  <figcaption>
                    <Text size="xs" className={styles.captionText}>
                      {image.caption}
                    </Text>
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </PageLayout>
    </Wrapper>
  );
};

export default StoryBlock;
